import { useId, useState } from 'react';
import type {
  ChangeEvent,
  InputHTMLAttributes,
  ReactNode,
  TextareaHTMLAttributes,
} from 'react';

interface Props extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'onChange'> {
  /** Testo dell'etichetta; ammette marcatura per evidenziare un valore da digitare. */
  etichetta: ReactNode;
  /** Nota esplicativa sotto il campo. */
  aiuto?: ReactNode;
  /** Messaggio di errore: sostituisce l'aiuto e colora etichetta e bordo. */
  errore?: string;
  /**
   * Segnala il campo come errato senza scrivere un messaggio sotto di esso: serve quando
   * il motivo è già spiegato altrove, e sotto al campo va invece una nota (6d, dove il
   * riquadro rosso spiega l'errore e sotto compare «Tentativo 2 di 5»).
   */
  invalido?: boolean;
  /**
   * `dato` — monospaziato, per matricole, date, orari, quantità (impostazione predefinita).
   * `testo` — proporzionale, per il testo discorsivo.
   */
  tipografia?: 'dato' | 'testo';
  /**
   * Numero di righe: con questa il controllo è un'area di testo invece di una riga sola.
   *
   * Sta qui e non in un componente a parte perché tutto il resto — etichetta, aiuto, stato
   * d'errore, il legame `aria-describedby` fra i due — è identico, e duplicarlo avrebbe
   * significato due posti in cui correggere il prossimo dettaglio di accessibilità. Cambia
   * il solo elemento che porta il testo.
   */
  righe?: number;
  /**
   * Accetta l'evento di entrambi gli elementi. Chi lo passa legge `e.target.value`, che
   * c'è in tutti e due; tipizzarlo sul solo `input` avrebbe costretto ogni chiamante di
   * un'area di testo a una conversione, per una differenza che non esiste all'uso.
   */
  onChange?: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}

/** Campo di testo con etichetta, aiuto e stato d'errore, come nel catalogo «Campi». */
export function Campo({
  etichetta,
  aiuto,
  errore,
  invalido = false,
  tipografia = 'dato',
  righe,
  className,
  disabled,
  type,
  ...resto
}: Props) {
  const id = useId();
  const idDescrizione = `${id}-descrizione`;
  const descrizione = errore ?? aiuto;
  const inErrore = Boolean(errore) || invalido;

  /**
   * Password in chiaro.
   *
   * Sta qui e non nelle singole schermate perché la password si digita in quattro punti —
   * accesso, sblocco e le tre caselle del primo accesso — e un campo che si comporta in
   * modo diverso a seconda di dove lo si incontra è un campo che si sbaglia. Lo stato è
   * per istanza: rivelarne una non rivela le altre.
   */
  const password = type === 'password';
  const [inChiaro, setInChiaro] = useState(false);

  const classiGruppo = [
    'campo',
    inErrore && 'campo--errore',
    disabled && 'campo--disabilitato',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const classiControllo = [
    'campo__controllo',
    tipografia === 'dato' && 'campo__controllo--dato',
    password && 'campo__controllo--con-occhio',
  ]
    .filter(Boolean)
    .join(' ');

  const controllo = righe ? (
    <textarea
      id={id}
      className={classiControllo}
      rows={righe}
      disabled={disabled}
      aria-invalid={inErrore ? true : undefined}
      aria-describedby={descrizione ? idDescrizione : undefined}
      // Gli attributi in comune fra i due elementi sono quelli che le schermate usano —
      // `value`, `placeholder`, `maxLength`, `onChange`; la conversione è confinata qui.
      {...(resto as TextareaHTMLAttributes<HTMLTextAreaElement>)}
    />
  ) : (
    <input
      id={id}
      className={classiControllo}
      disabled={disabled}
      type={password && inChiaro ? 'text' : type}
      aria-invalid={inErrore ? true : undefined}
      aria-describedby={descrizione ? idDescrizione : undefined}
      {...resto}
    />
  );

  return (
    <div className={classiGruppo}>
      <label className="campo__etichetta" htmlFor={id}>
        {etichetta}
      </label>

      {password ? (
        <div className="campo__guscio">
          {controllo}
          <button
            type="button"
            className="campo__occhio"
            // Il pulsante non invia nulla e non cambia il valore: lo stato premuto dice
            // solo se la password è visibile in questo momento.
            aria-pressed={inChiaro}
            aria-label={inChiaro ? 'Nascondi la password' : 'Mostra la password'}
            title={inChiaro ? 'Nascondi la password' : 'Mostra la password'}
            disabled={disabled}
            onClick={() => setInChiaro((v) => !v)}
          >
            <Occhio barrato={inChiaro} />
          </button>
        </div>
      ) : (
        controllo
      )}

      {errore ? (
        <span id={idDescrizione} className="campo__errore">
          {errore}
        </span>
      ) : aiuto ? (
        <span id={idDescrizione} className="campo__aiuto">
          {aiuto}
        </span>
      ) : null}
    </div>
  );
}

/**
 * L'occhio, nello stesso tratto delle icone del guscio: 1,8 di spessore e terminali
 * arrotondati. Barrato quando la password è in chiaro, perché il segno deve dire cosa
 * accade premendo, non cosa sta accadendo ora.
 */
function Occhio({ barrato }: { barrato: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="21"
      height="21"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12z" />
      <circle cx="12" cy="12" r="2.8" />
      {barrato ? <path d="M4 20 20 4" /> : null}
    </svg>
  );
}
